import {
  AddCircleRounded,
  CardGiftcardSharp,
  ContentCopyOutlined,
  DeleteOutlined,
  EditOutlined,
  LocationOffSharp,
  PersonOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";

import { Customer, CustomerAddress, CustomerECPShipTo } from "../../types/crm";
import { formatThaiPhone } from "../../utils/phone";
import EditPointsDialog from "./EditPointsDialog";

type PropsType = {
  customer: Customer;
  addresses?: CustomerAddress[];
  addressLoading?: boolean;
  shipToData?: CustomerECPShipTo[];
  shipToLoading?: boolean;
  onEditDetail?: () => void;
  onAddAddress?: () => void;
  onEditAddress?: (address: CustomerAddress) => void;
  onSetDefaultAddress?: (address: CustomerAddress) => void;
  onDeleteAddress?: (address: CustomerAddress) => void;
  onEditPoints?: (data: {
    mode: "increase" | "decrease";
    points: number;
    reason: string;
    expirationDate?: string;
    sendNotification: boolean;
  }) => Promise<void>;
};

const labelSx = {
  fontSize: 13,
  lineHeight: 1.4,
  color: "#8E95A2",
  mb: 0.25,
};

const valueSx = {
  fontSize: 15,
  lineHeight: 1.5,
  color: "#101828",
  fontWeight: 500,
};

const InfoField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box>
    <Typography sx={labelSx}>{label}</Typography>
    {children}
  </Box>
);

const getStatusColor = (status?: string) => {
  if (status === "active") return { bg: "#E3F5E3", color: "#2D6830" };
  if (status === "rejected" || status === "suspended")
    return { bg: "#FDECEA", color: "#BE2217" };
  if (status === "inactive" || status === "in_monitor")
    return { bg: "#FFFBC5", color: "#BB6902" };
  return { bg: "#EDEEF1", color: "#6B7280" };
};

const getStatusLabel = (status?: string) => {
  if (status === "inactive") return "Pending Approval";
  if (status === "in_monitor") return "In Monitor";
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
};

const CustomerDetailView = ({
  customer,
  addresses = [],
  addressLoading,
  shipToData,
  shipToLoading,
  onEditDetail,
  onAddAddress,
  onEditAddress,
  onSetDefaultAddress,
  onDeleteAddress,
  onEditPoints,
}: PropsType) => {
  const [editPointsOpen, setEditPointsOpen] = useState(false);
  const fullName =
    `${customer.name || ""} ${customer.surname || ""}`.trim() || "-";
  const points = Number(customer.points || 0).toLocaleString();
  const createdDate = customer.created_at
    ? dayjs(customer.created_at).format("DD/MM/YYYY HH:mm")
    : "-";

  const nearestExpiry = customer.points_breakdown?.expiring_points
    ?.slice()
    .sort(
      (a, b) =>
        new Date(a.expires_on).getTime() - new Date(b.expires_on).getTime(),
    )[0];
  const validUntilText = nearestExpiry
    ? dayjs(nearestExpiry.expires_on).format("DD/MM/YYYY")
    : "-";
  const lastLoginDate = customer.last_login
    ? dayjs(customer.last_login).format("DD/MM/YYYY HH:mm")
    : "-";

  const latestStatusHistory =
    customer.status_history
      ?.slice()
      .sort(
        (a, b) =>
          new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime(),
      )[0] ?? null;

  const lineVerification = customer.line_name
    ? { label: "Active", bg: "#E3F5E3", color: "#2D6830" }
    : { label: "Inactive", bg: "#EDEEF1", color: "#6B7280" };

  const statusColor = getStatusColor(customer.status);

  const copyEcpCode = () => {
    if (
      !customer.ecp_code ||
      typeof navigator === "undefined" ||
      !navigator.clipboard
    ) {
      return;
    }
    void navigator.clipboard
      .writeText(customer.ecp_code)
      .catch(() => undefined);
  };

  return (
    <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5}>
      {/* Customer Info Panel */}
      <Paper
        variant="outlined"
        sx={{
          p: 0,
          borderRadius: 2,
          overflow: "hidden",
          flex: 1,
          minWidth: 0,
          borderColor: "#E5E7EB",
        }}
      >
        {/* Header with avatar and name */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2, borderBottom: "1px solid #E5E7EB" }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "#F3F4F6",
                color: "#6B7280",
              }}
            >
              <PersonOutlined sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography
                sx={{ fontSize: 18, fontWeight: 600, color: "#111827", lineHeight: 1.3 }}
              >
                {fullName}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#8E95A2" }}>
                {formatThaiPhone(customer.phone, "No phone")}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="outlined"
            startIcon={<EditOutlined sx={{ fontSize: 16 }} />}
            onClick={onEditDetail}
            sx={{
              height: 36,
              borderRadius: "96px",
              borderColor: "#D1D5DB",
              color: "#374151",
              fontSize: 14,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" },
            }}
          >
            Edit
          </Button>
        </Stack>

        <Stack sx={{ p: 2.5 }} spacing={2.5}>
          {/* Points Card */}
          <Box
            sx={{
              background: "linear-gradient(180deg, #333333 0%, #000000 100%)",
              border: "1px solid rgba(255,255,255,0.16)",
              borderRadius: "4px",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <CardGiftcardSharp sx={{ color: "#FFFFFF", fontSize: 32 }} />
              <Stack spacing={0.5}>
                <Typography
                  sx={{ color: "#FFFFFF", fontSize: 16, fontWeight: 700, lineHeight: 1.5 }}
                >
                  {points} Points
                </Typography>
                <Typography sx={{ color: "#FFFFFF", fontSize: 14, lineHeight: 1.4 }}>
                  Valid until {validUntilText}
                </Typography>
              </Stack>
            </Stack>

            <Button
              variant="contained"
              onClick={() => setEditPointsOpen(true)}
              disabled={customer.status === "inactive"}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#FFFFFF",
                color: "#111111",
                fontWeight: 700,
                fontSize: 16,
                textTransform: "none",
                boxShadow: "none",
                px: 3,
                "&:hover": {
                  bgcolor: "#F3F4F6",
                  boxShadow: "none",
                },
              }}
            >
              Edit Points
            </Button>
          </Box>

          {/* Customer Details Grid */}
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <InfoField label="ECP Code">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography
                      sx={{
                        ...valueSx,
                        fontFamily: "monospace",
                        letterSpacing: 0.5,
                      }}
                    >
                      {customer.ecp_code || "-"}
                    </Typography>
                    {customer.ecp_code && (
                      <Tooltip title="Copy" arrow>
                        <IconButton
                          size="small"
                          onClick={copyEcpCode}
                          sx={{
                            p: 0.25,
                            color: "#9CA3AF",
                            "&:hover": { color: "#6B7280" },
                          }}
                        >
                          <ContentCopyOutlined sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </InfoField>
                <InfoField label="Store Name">
                  <Typography sx={valueSx}>
                    {customer.shop_name || "-"}
                  </Typography>
                </InfoField>
                <InfoField label="Line User ID">
                  <Typography
                    sx={{
                      ...valueSx,
                      fontSize: 13,
                      fontFamily: "monospace",
                      color: "#6B7280",
                    }}
                  >
                    {customer.id || "-"}
                  </Typography>
                </InfoField>
                <InfoField label="Line Name">
                  <Typography sx={valueSx}>
                    {customer.line_name || "-"}
                  </Typography>
                </InfoField>
                <InfoField label="Line Verification">
                  <Chip
                    size="small"
                    label={lineVerification.label}
                    sx={{
                      bgcolor: lineVerification.bg,
                      color: lineVerification.color,
                      fontSize: 12,
                      fontWeight: 500,
                      borderRadius: "6px",
                      height: 24,
                    }}
                  />
                </InfoField>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <InfoField label="Email">
                  <Typography sx={valueSx}>
                    {customer.email || "-"}
                  </Typography>
                </InfoField>
                <InfoField label="Phone Number">
                  <Typography sx={valueSx}>
                    {formatThaiPhone(customer.phone, "-")}
                  </Typography>
                </InfoField>
                <InfoField label="Created Date">
                  <Typography sx={valueSx}>{createdDate}</Typography>
                </InfoField>
                <InfoField label="Last Login">
                  <Typography sx={valueSx}>{lastLoginDate}</Typography>
                </InfoField>
                <InfoField label="Customer Status">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      size="small"
                      label={getStatusLabel(customer.status)}
                      sx={{
                        bgcolor: statusColor.bg,
                        color: statusColor.color,
                        fontSize: 12,
                        fontWeight: 500,
                        borderRadius: "6px",
                        height: 24,
                      }}
                    />
                  </Stack>
                  {latestStatusHistory?.remark && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#9CA3AF",
                        mt: 0.5,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        fontStyle: "italic",
                      }}
                    >
                      {latestStatusHistory.remark}
                    </Typography>
                  )}
                </InfoField>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      {/* Shipping Address Panel */}
      <Paper
        variant="outlined"
        sx={{
          p: 0,
          borderRadius: 2,
          overflow: "hidden",
          flex: 1,
          minHeight: 580,
          minWidth: 0,
          borderColor: "#E5E7EB",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={1}
          sx={{ px: 2.5, py: 2, borderBottom: "1px solid #E5E7EB" }}
        >
          <Typography
            sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.4, color: "#111827" }}
          >
            Shipping Address
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddCircleRounded sx={{ fontSize: 18 }} />}
            onClick={onAddAddress}
            sx={{
              height: 36,
              borderRadius: "96px",
              bgcolor: "#111827",
              fontWeight: 700,
              fontSize: 14,
              textTransform: "none",
              whiteSpace: "nowrap",
              boxShadow: "none",
              "&:hover": { bgcolor: "#1F2937", boxShadow: "none" },
            }}
          >
            Add Address
          </Button>
        </Stack>

        {addressLoading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ flex: 1, minHeight: 320 }}
          >
            <CircularProgress size={36} />
          </Stack>
        ) : addresses.length === 0 && !shipToData?.length ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.5}
            sx={{ flex: 1, minHeight: 320, px: 3, py: 6 }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "12px",
                bgcolor: "#F3F4F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocationOffSharp sx={{ fontSize: 28, color: "#9CA3AF" }} />
            </Box>
            <Typography
              sx={{ color: "#6B7280", fontSize: 14, textAlign: "center" }}
            >
              No store address found for the referenced ECP Code.
            </Typography>
          </Stack>
        ) : (
          <>
            {addresses.length > 0 && (
              <Box sx={{ overflowY: "auto" }}>
                {addresses.map((addr, idx) => (
              <Box key={addr.id}>
                {idx > 0 && <Divider />}
                <Stack spacing={1.5} sx={{ px: 2.5, py: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: "#111827",
                          lineHeight: 1.4,
                        }}
                      >
                        {addr.shop_name || `${addr.name} ${addr.surname}`}
                      </Typography>
                      {addr.phone && (
                        <Typography
                          sx={{
                            fontSize: 14,
                            lineHeight: 1.5,
                            color: "#6B7280",
                            mt: 0.25,
                          }}
                        >
                          {formatThaiPhone(addr.phone)}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onDeleteAddress?.(addr)}
                          sx={{
                            color: "#9CA3AF",
                            "&:hover": { color: "#EF4444", bgcolor: "#FEF2F2" },
                          }}
                        >
                          <DeleteOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onEditAddress?.(addr)}
                          sx={{
                            color: "#9CA3AF",
                            "&:hover": { color: "#3B82F6", bgcolor: "#EFF6FF" },
                          }}
                        >
                          <EditOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Typography
                    sx={{ fontSize: 14, lineHeight: 1.6, color: "#374151" }}
                  >
                    {[
                      addr.address,
                      addr.sub_district,
                      addr.district,
                      addr.province,
                      addr.zip_code,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </Typography>

                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {addr.ecp_code && (
                      <Chip
                        size="small"
                        label="ECP Linked"
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 500,
                          bgcolor: "#F3F4F6",
                          color: "#374151",
                          borderRadius: "6px",
                        }}
                      />
                    )}
                    {addr.default ? (
                      <Chip
                        size="small"
                        label="Default"
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 500,
                          bgcolor: "#E3F5E3",
                          color: "#2D6830",
                          borderRadius: "6px",
                        }}
                      />
                    ) : (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => onSetDefaultAddress?.(addr)}
                        sx={{
                          minWidth: "unset",
                          px: 0.75,
                          py: 0,
                          height: 22,
                          color: "#3B82F6",
                          fontSize: 11,
                          fontWeight: 500,
                          textTransform: "none",
                          "&:hover": { bgcolor: "#EFF6FF" },
                        }}
                      >
                        Set as Default
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Box>
            )}
          </>
        )}

        {/* Ship-To Addresses — read-only, inside same Paper */}
        {(shipToLoading || (shipToData && shipToData.length > 0)) && (
          <>
            <Divider />
            {shipToLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 80 }}>
                <CircularProgress size={28} />
              </Stack>
            ) : (
              <Box sx={{ overflowY: "auto" }}>
                {(shipToData ?? []).map((ecp, ecpIdx) => (
                  <Box key={ecp.id}>
                    {ecpIdx > 0 && <Divider />}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ px: 2.5, pt: 1.5, pb: ecp.addresses?.length ? 0.5 : 1.5 }}
                    >
                      <Chip
                        size="small"
                        label={ecp.code}
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 600,
                          bgcolor: "#EFF6FF",
                          color: "#1D4ED8",
                          borderRadius: "6px",
                        }}
                      />
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                        {ecp.name}
                      </Typography>
                    </Stack>
                    {ecp.addresses?.length ? (
                      ecp.addresses.map((addr, idx) => (
                        <Box key={addr.id}>
                          {idx > 0 && <Divider sx={{ mx: 2.5 }} />}
                          <Stack spacing={1} sx={{ px: 2.5, py: 1.5 }}>
                            <Box>
                              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827", lineHeight: 1.4 }}>
                                {(addr.shop_name && addr.shop_name !== ecp.name)
                                  ? addr.shop_name
                                  : `${addr.name} ${addr.surname}`.trim() || addr.shop_name}
                              </Typography>
                              {addr.phone && (
                                <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: "#6B7280", mt: 0.25 }}>
                                  {formatThaiPhone(addr.phone)}
                                </Typography>
                              )}
                            </Box>
                            <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: "#374151" }}>
                              {[addr.address, addr.sub_district, addr.district, addr.province, addr.zip_code]
                                .filter(Boolean)
                                .join(", ")}
                            </Typography>
                          </Stack>
                        </Box>
                      ))
                    ) : (
                      <Typography sx={{ px: 2.5, pb: 1.5, fontSize: 13, color: "#9CA3AF" }}>
                        No addresses
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Paper>
        {onEditPoints && (
        <EditPointsDialog
          open={editPointsOpen}
          onClose={() => setEditPointsOpen(false)}
          onSubmit={onEditPoints}
        />
      )}
    </Stack>
  );
};

export default CustomerDetailView;
